import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, GridConfig, GridRow } from '@inavista/components/grid';

// Order (Checkout) popup (Execution tab → Add to Cart / Create Purchase Order, the parts catalog, the cart, My Orders,
// Purchase Orders, the dashboard and the linked-record opener) → its three grids on @inavista/components: the items
// being ordered (Details tab), Ordered Items and Tracking Updates (Tracking tab). Same columns, labels, order, widths,
// alignment, formats, links, status pills and row actions as the Syncfusion grids they replace. Rows are the popup's
// own arrays (recievedItems, orderedTabDataSource, trackingTabDataSource), served by LocalRowsGridApi: no endpoint.
// None of the three old grids had a toolbar (no Search, Columns, Excel or Clear filters button): neither do these.
// No paging (Martin): the grids scroll.

export const CHECKOUT_ITEMS_URL = 'page://cart-checkout-items';
export const ORDERED_ITEMS_URL = 'page://cart-checkout-ordered-items';
export const TRACKING_UPDATES_URL = 'page://cart-checkout-tracking-updates';

export const CHECKOUT_ITEMS_PERSISTENCE_KEY = 'rims.service.cart-checkout-items';
export const ORDERED_ITEMS_PERSISTENCE_KEY = 'rims.service.cart-checkout-ordered-items';
export const TRACKING_UPDATES_PERSISTENCE_KEY = 'rims.service.cart-checkout-tracking-updates';

export const CHECKOUT_ITEMS_COLUMNS: [string, string][] = [
  ['item', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['quantity', 'decimal'],
  ['itemPrice', 'decimal'],
  ['grossAmount', 'decimal'],
  ['itemDiscount', 'decimal'],
  ['netPrice', 'decimal'],
];

export const ORDERED_ITEMS_COLUMNS: [string, string][] = [
  ['item', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['quantity', 'decimal'],
  ['isDirectDelivery', 'nvarchar'],
  ['orderDateLN', 'datetime'],
  ['status', 'nvarchar'],
];

export const TRACKING_UPDATES_COLUMNS: [string, string][] = [
  ['item', 'nvarchar'],
  ['description', 'nvarchar'],
  ['shipQty', 'decimal'],
  ['status', 'nvarchar'],
  ['shipmentIdLN', 'nvarchar'],
  ['shippedDateLN', 'datetime'],
  ['trackingId', 'nvarchar'],
];

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
// A cancelled line's Cancel icon: red (the old .color-red on the disabled button).
const RED = '#ff0000';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';
// fa-times-circle.
const CANCEL_ICON = '<circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />';

const LEFT = { horizontal: 'left' as const, header: { horizontal: 'left' as const } };
const DATE_FORMAT = 'd MMM, y';
// A number column without a format: the value as it comes, no thousands separator (Syncfusion's raw value).
const RAW_NUMBER: Intl.NumberFormatOptions = { useGrouping: false, maximumFractionDigits: 20 };
// Syncfusion 'n2'.
const N2: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

/** Why a row's action is disabled, or null when it is enabled. */
export type LockReason = (row: GridRow) => string | null;

// What the three grids share: no toolbar, as before; Syncfusion's multi-sort (Ctrl+click); the row height of the
// other Service Order popup grids.
function base(columns: [string, string][]) {
  const order = columns.map(([c]) => c);
  return {
    clientSide: true,
    // A synthetic key (the rows are the page's own objects); not a column.
    keyColumn: 'rowId',
    rowHeight: 30,
    defaultColumnOrder: order,
    // Every column and header left, as the old grids.
    columnAlign: Object.fromEntries(order.map(c => [c, LEFT])),
    stretchColumns: true,
    multiSort: true,
    enableToolbar: false,
    enableSearch: false,
    enableColumnsPicker: false,
    enableExport: false,
    enableSavedViews: false,
    enableViewsPicker: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    emptyStateMessage: 'No records to display',
  } satisfies Partial<GridConfig>;
}

/** The fixed-width columns keep their width; the others share what is left, as Syncfusion did. */
function fixed(columns: string[]) {
  return Object.fromEntries(columns.map(c => [c, { noStretch: true }]));
}

// ---- Details tab: the items being ordered ------------------------------------------------------------------------

export function buildCheckoutItemsActionIcons(lockReason: LockReason): ActionIconDef[] {
  return [{
    key: 'Delete Item', label: 'Delete Item', icon: TRASH_ICON,
    colorFn: row => (lockReason(row) ? INACTIVE : BLACK),
    disabledFn: row => !!lockReason(row),
    labelFn: row => lockReason(row) ?? 'Delete Item',
  }];
}

export function buildCheckoutItemsGridConfig(): GridConfig {
  return {
    ...base(CHECKOUT_ITEMS_COLUMNS),
    apiBaseUrl: CHECKOUT_ITEMS_URL,
    columnLabels: {
      item: 'Item.', itemDescription: 'Description', quantity: 'Order Qty', itemPrice: 'Price (USD)',
      grossAmount: 'Gross Amount (USD)', itemDiscount: 'Discount (USD)', netPrice: 'Net Amount (USD)',
    },
    // 120 as before; Item, Order Qty and Discount had no width and took the rest.
    columnWidths: { item: 150, itemDescription: 120, quantity: 150, itemPrice: 120, grossAmount: 120, itemDiscount: 150, netPrice: 120 },
    columnSizing: fixed(['itemDescription', 'itemPrice', 'grossAmount', 'netPrice']),
    columnTypes: { quantity: 'decimal', itemPrice: 'decimal', grossAmount: 'decimal', itemDiscount: 'decimal', netPrice: 'decimal' },
    columnNumberFormats: { quantity: RAW_NUMBER, itemPrice: N2, grossAmount: N2, itemDiscount: N2, netPrice: N2 },
    // Item and Description open the item, as before.
    hyperlinkColumns: ['item', 'itemDescription'],
    // The old header's red * on Item. (read-only grid).
    requiredColumns: ['item'],
    requiredMarkers: 'always',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CHECKOUT_ITEMS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}

// ---- Tracking tab: Ordered Items and Tracking Updates ------------------------------------------------------------

/** The old Cancel command: grey and inert when locked, red and inert on a cancelled line. */
export function buildCancelActionIcons(lockReason: LockReason): ActionIconDef[] {
  return [{
    key: 'Cancel', label: 'Cancel', icon: CANCEL_ICON,
    colorFn: row => (row['status'] === 'Canceled' ? RED : lockReason(row) ? INACTIVE : BLACK),
    disabledFn: row => !!lockReason(row),
    labelFn: row => lockReason(row) ?? 'Cancel',
  }];
}

export function buildOrderedItemsGridConfig(statusColor: (value: unknown) => string): GridConfig {
  return {
    ...base(ORDERED_ITEMS_COLUMNS),
    apiBaseUrl: ORDERED_ITEMS_URL,
    columnLabels: {
      item: 'Item.', itemDescription: 'Description', quantity: 'Quantity', isDirectDelivery: 'Direct Delivery',
      orderDateLN: 'LN Order Date', status: 'Status',
    },
    // 120 as before; Quantity, LN Order Date and Status had no width and took the rest.
    columnWidths: { item: 120, itemDescription: 120, quantity: 150, isDirectDelivery: 120, orderDateLN: 150, status: 150 },
    columnSizing: fixed(['item', 'itemDescription', 'isDirectDelivery']),
    columnTypes: { quantity: 'decimal', orderDateLN: 'date' },
    columnNumberFormats: { quantity: RAW_NUMBER },
    columnDateFormats: { orderDateLN: DATE_FORMAT },
    columnColors: { status: statusColor },
    // Item and Description open the item, as before.
    hyperlinkColumns: ['item', 'itemDescription'],
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ORDERED_ITEMS_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}

export function buildTrackingUpdatesGridConfig(statusColor: (value: unknown) => string, trackingIdCell: TemplateRef<unknown>): GridConfig {
  return {
    ...base(TRACKING_UPDATES_COLUMNS),
    apiBaseUrl: TRACKING_UPDATES_URL,
    columnLabels: {
      item: 'Item.', description: 'Description', shipQty: 'Quantity', status: 'Status', shipmentIdLN: 'LN Shipment Id',
      shippedDateLN: 'LN Shipment Date', trackingId: 'Tracking Id',
    },
    // 120 as before; Quantity, LN Shipment Id and LN Shipment Date had no width and took the rest.
    columnWidths: { item: 120, description: 120, shipQty: 150, status: 120, shipmentIdLN: 150, shippedDateLN: 150, trackingId: 120 },
    columnSizing: fixed(['item', 'description', 'status', 'trackingId']),
    columnTypes: { shipQty: 'decimal', shippedDateLN: 'date' },
    columnNumberFormats: { shipQty: RAW_NUMBER },
    columnDateFormats: { shippedDateLN: DATE_FORMAT },
    columnColors: { status: statusColor },
    // The tracking number opens the carrier's page in a new tab, with its external-link icon, as before.
    cellTemplates: { trackingId: trackingIdCell },
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: TRACKING_UPDATES_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
